import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { 
  Plus, Search, ShoppingCart, User, Package, Trash2, 
  LogOut, DollarSign, FileText, Check, X, CreditCard, Loader2, Printer, Receipt, ClipboardList,
  ChevronLeft, ChevronRight, Calendar, Edit, Eye, CalendarClock
} from 'lucide-react';
import { formatCPF, formatPhone, formatCNPJ, validateCPF, validateCNPJ } from '@/utils/formatters';
import { ProductModal } from '@/components/ProductModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ESTADOS_BRASIL = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  // Extrai apenas a parte da data (YYYY-MM-DD) para evitar problemas de timezone
  const datePart = String(dateStr).split('T')[0];
  const [year, month, day] = datePart.split('-');
  if (!year || !month || !day) return '-';
  return `${day}/${month}/${year}`;
};

export default function Vendas() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Navegação por etapas
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  
  // Refs para scroll automático
  const customerListRef = useRef(null);
  const productListRef = useRef(null);
  const productSearchRef = useRef(null);
  const quantityInputRef = useRef(null);
  
  // Dados
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [tiposMadeira, setTiposMadeira] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [minhasVendas, setMinhasVendas] = useState([]);
  const [nextOrderNumber, setNextOrderNumber] = useState('');
  
  // Tipo de documento
  const [tipoDocumento, setTipoDocumento] = useState('venda');
  
  // Estados do formulário
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [searchCustomer, setSearchCustomer] = useState('');
  const [searchProduct, setSearchProduct] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [dataEntrega, setDataEntrega] = useState(new Date().toISOString().split('T')[0]);
  const [dataPedido, setDataPedido] = useState(new Date().toISOString().split('T')[0]);
  
  // Desconto
  const [tipoDesconto, setTipoDesconto] = useState('');
  const [valorDesconto, setValorDesconto] = useState(0);
  
  // Pagamentos
  const [pagamentos, setPagamentos] = useState([]);
  const [pagamentoAtual, setPagamentoAtual] = useState({
    forma_pagamento: 'PIX',
    valor: '',
    conta_id: '',
    bandeira: '',
    parcelas: 1,
    nsu: '',
    observacao: '',
    data_pagamento: new Date().toISOString().split('T')[0]
  });
  
  // Modais
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [showNewProduct, setShowNewProduct] = useState(false);
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [salvandoCliente, setSalvandoCliente] = useState(false);
  
  // Parcelamento (Contas a Receber)
  const [parcelamentoAtivo, setParcelamentoAtivo] = useState(false);
  const [parcelamentoConfig, setParcelamentoConfig] = useState({
    num_parcelas: 2,
    valor_entrada: '',
    primeira_parcela_data: '',
    modo_data_parcelas: 'automatico',
    parcelas_customizadas: []
  });
  
  // Histórico do cliente
  const [showHistorico, setShowHistorico] = useState(false);
  const [historicoLoading, setHistoricoLoading] = useState(false);
  const [historicoPedidos, setHistoricoPedidos] = useState([]);
  const [salvandoProduto, setSalvandoProduto] = useState(false);
  const [finalizando, setFinalizando] = useState(false);
  
  // Navegação por teclado
  const [highlightedCustomerIndex, setHighlightedCustomerIndex] = useState(-1);
  const [highlightedProductIndex, setHighlightedProductIndex] = useState(-1);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  
  // Produto selecionado para adicionar (igual ao sistema principal)
  const [produtoSelecionado, setProdutoSelecionado] = useState(null);
  const [produtoQtd, setProdutoQtd] = useState(1);
  const [produtoPreco, setProdutoPreco] = useState(0);
  const [editingItemId, setEditingItemId] = useState(null);
  
  // Edição de pedido
  const [editingOrder, setEditingOrder] = useState(null);
  const [deletingOrderId, setDeletingOrderId] = useState(null);
  const [deletingOrcamentoId, setDeletingOrcamentoId] = useState(null);
  
  // Aba ativa: 'nova' ou 'historico'
  const [abaAtiva, setAbaAtiva] = useState('nova');
  
  // Pesquisa no histórico
  const [searchHistorico, setSearchHistorico] = useState('');
  
  // Orçamentos separados
  const [meusOrcamentos, setMeusOrcamentos] = useState([]);
  const [nextOrcamentoNumber, setNextOrcamentoNumber] = useState('ORC-0001');
  const [convertendoId, setConvertendoId] = useState(null);
  
  // Novo cliente
  const [novoCliente, setNovoCliente] = useState({
    nome: '', email: '', telefone: '', cpf: '', endereco: '', cidade: '', estado: '', cep: '', tipo: 'PF', bairro: ''
  });
  const [buscandoCEP, setBuscandoCEP] = useState(false);
  const [buscandoCNPJ, setBuscandoCNPJ] = useState(false);
  
  // Novo produto
  const [novoProduto, setNovoProduto] = useState({
    nome: '', codigo: '', ncm: '', descricao: '', custo: '', preco: '', categoria: '', unidade: 'UN',
    estoque_minimo: '', observacoes: '', volume_m3: '', tipo_madeira_id: '', mao_de_obra: '', pintura: '',
    pecas_madeira: []
  });
  const [novaPecaMadeira, setNovaPecaMadeira] = useState({ comprimento: '', largura: '', espessura: '', quantidade: '1' });

  // Funções de cálculo de madeira
  const calcularVolumePeca = (peca) => {
    const comp = parseFloat(peca.comprimento) || 0;
    const larg = parseFloat(peca.largura) || 0;
    const esp = parseFloat(peca.espessura) || 0;
    const qtd = parseInt(peca.quantidade) || 1;
    return ((comp * larg * esp) / 1000000) * qtd;
  };

  const calcularVolumeTotal = (pecas) => {
    return (pecas || []).reduce((total, peca) => total + calcularVolumePeca(peca), 0);
  };

  const calcularCustoMadeira = (pecas, tipoMadeiraId) => {
    const tipo = tiposMadeira.find(t => t.id === tipoMadeiraId);
    if (!tipo) return 0;
    const volumeTotal = calcularVolumeTotal(pecas);
    return volumeTotal * tipo.preco_m3;
  };

  const handleAddPecaMadeira = () => {
    if (!novaPecaMadeira.comprimento || !novaPecaMadeira.largura || !novaPecaMadeira.espessura) {
      toast.error('Preencha comprimento, largura e espessura');
      return;
    }
    const novaPecaComId = { ...novaPecaMadeira, id: Date.now() };
    const novasPecas = [...(novoProduto.pecas_madeira || []), novaPecaComId];
    const volumeTotal = calcularVolumeTotal(novasPecas);
    const custoMadeira = calcularCustoMadeira(novasPecas, novoProduto.tipo_madeira_id);
    const maoDeObra = parseFloat(novoProduto.mao_de_obra) || 0;
    const pintura = parseFloat(novoProduto.pintura) || 0;
    const custoTotal = custoMadeira + maoDeObra + pintura;
    
    setNovoProduto({
      ...novoProduto,
      pecas_madeira: novasPecas,
      volume_m3: volumeTotal.toFixed(6),
      custo: custoTotal.toFixed(2)
    });
    setNovaPecaMadeira({ comprimento: '', largura: '', espessura: '', quantidade: '1' });
    toast.success('Peça adicionada!');
  };

  const handleRemovePecaMadeira = (pecaId) => {
    const novasPecas = (novoProduto.pecas_madeira || []).filter(p => p.id !== pecaId);
    const volumeTotal = calcularVolumeTotal(novasPecas);
    const custoMadeira = calcularCustoMadeira(novasPecas, novoProduto.tipo_madeira_id);
    const maoDeObra = parseFloat(novoProduto.mao_de_obra) || 0;
    const pintura = parseFloat(novoProduto.pintura) || 0;
    const custoTotal = custoMadeira + maoDeObra + pintura;
    
    setNovoProduto({
      ...novoProduto,
      pecas_madeira: novasPecas,
      volume_m3: volumeTotal.toFixed(6),
      custo: custoTotal.toFixed(2)
    });
  };

  // Verificar autenticação
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      window.location.href = '/vendas/login';
      return;
    }
    
    try {
      const parsedUser = JSON.parse(userData);
      // Permitir acesso se:
      // - role === 'vendedor'
      // - role === 'admin'
      // - acesso_vendas === true
      const hasAccess = parsedUser.role === 'vendedor' || 
                        parsedUser.role === 'admin' || 
                        parsedUser.acesso_vendas === true;
      
      if (!hasAccess) {
        toast.error('Você não tem permissão para acessar o módulo de vendas');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/vendas/login';
        return;
      }
      setUser(parsedUser);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } catch (e) {
      window.location.href = '/vendas/login';
    }
  }, []);

  useEffect(() => {
    if (user) fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [customersRes, productsRes, accountsRes, tiposMadeiraRes, categoriesRes, vendasRes, orcamentosRes, nextNumRes, nextOrcNumRes] = await Promise.all([
        axios.get(`${API}/customers`, { params: { all_data: true } }),
        axios.get(`${API}/products`, { params: { all_data: true } }),
        axios.get(`${API}/bank-accounts`),
        axios.get(`${API}/tipos-madeira`),
        axios.get(`${API}/settings/categories/product`),
        axios.get(`${API}/orders`, { params: { all_data: true, months: 1 } }),
        axios.get(`${API}/orcamentos`).catch(() => ({ data: [] })),
        axios.get(`${API}/orders/next-number`),
        axios.get(`${API}/orcamentos/next-number`).catch(() => ({ data: { numero_formatado: 'ORC-0001' } }))
      ]);
      
      setCustomers(Array.isArray(customersRes.data) ? customersRes.data : (customersRes.data.customers || []));
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : (productsRes.data.products || []));
      setBankAccounts(accountsRes.data || []);
      setTiposMadeira(tiposMadeiraRes.data || []);
      setProductCategories(categoriesRes.data?.categories || []);
      setNextOrderNumber(nextNumRes.data?.numero_formatado || 'PED-0001');
      setNextOrcamentoNumber(nextOrcNumRes.data?.numero_formatado || 'ORC-0001');
      
      // Vendas (pedidos)
      const vendasData = Array.isArray(vendasRes.data) ? vendasRes.data : (vendasRes.data.orders || []);
      const vendasOrdenadas = vendasData
        .filter(v => v.status !== 'Orçamento' && v.forma_pagamento !== 'Orçamento')
        .sort((a, b) => new Date(b.createdAt || b.created_at || 0) - new Date(a.createdAt || a.created_at || 0))
        .slice(0, 20);
      setMinhasVendas(vendasOrdenadas);
      
      // Orçamentos separados
      const orcamentosData = Array.isArray(orcamentosRes.data) ? orcamentosRes.data : [];
      const orcamentosOrdenados = orcamentosData
        .sort((a, b) => new Date(b.createdAt || b.created_at || 0) - new Date(a.createdAt || a.created_at || 0))
        .slice(0, 20);
      setMeusOrcamentos(orcamentosOrdenados);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Função para remover acentos (busca sem acento)
  const removeAccents = (str) => {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };

  // Filtros
  const filteredCustomers = customers.filter(c => {
    const searchNormalized = removeAccents(searchCustomer.toLowerCase());
    const nomeNormalized = removeAccents((c.nome || c.name || '').toLowerCase());
    const telefone = (c.telefone || c.phone || '');
    const cpfCnpj = (c.cpf_cnpj || '');
    
    return nomeNormalized.includes(searchNormalized) ||
           telefone.includes(searchCustomer) ||
           cpfCnpj.includes(searchCustomer);
  }).slice(0, 10);

  const filteredProducts = products.filter(p => {
    const searchNormalized = removeAccents(searchProduct.toLowerCase());
    const nomeNormalized = removeAccents((p.nome || p.name || '').toLowerCase());
    const codigoNormalized = removeAccents((p.codigo || p.code || '').toLowerCase());
    
    return nomeNormalized.includes(searchNormalized) ||
           codigoNormalized.includes(searchNormalized);
  }).slice(0, 15);

  // Funções de carrinho
  const selecionarProduto = (product) => {
    setProdutoSelecionado(product);
    setProdutoQtd(1);
    setProdutoPreco(product.preco_base || product.preco_venda || product.price || 0);
    setSearchProduct('');
    setShowProductSearch(false);
    setEditingItemId(null);
    
    // Focar no campo de quantidade
    setTimeout(() => {
      if (quantityInputRef.current) {
        quantityInputRef.current.focus();
        quantityInputRef.current.select();
      }
    }, 100);
  };
  
  const addToCart = () => {
    if (!produtoSelecionado) return;
    
    const existing = cartItems.find(item => item.id === produtoSelecionado.id);
    if (existing) {
      setCartItems(cartItems.map(item => 
        item.id === produtoSelecionado.id ? { ...item, quantity: item.quantity + produtoQtd, price: produtoPreco } : item
      ));
    } else {
      setCartItems([...cartItems, { 
        ...produtoSelecionado, 
        name: produtoSelecionado.nome || produtoSelecionado.name,
        code: produtoSelecionado.codigo || produtoSelecionado.code,
        quantity: produtoQtd,
        price: produtoPreco,
        unit: produtoSelecionado.unidade || produtoSelecionado.unit || 'UN'
      }]);
    }
    setProdutoSelecionado(null);
    setProdutoQtd(1);
    setProdutoPreco(0);
    toast.success(`${produtoSelecionado.nome || produtoSelecionado.name} adicionado`);
    
    // Focar no campo de busca para adicionar novo produto
    setTimeout(() => {
      if (productSearchRef.current) {
        productSearchRef.current.focus();
      }
    }, 100);
  };

  const editCartItem = (item) => {
    setProdutoSelecionado(item);
    setProdutoQtd(item.quantity);
    setProdutoPreco(item.price);
    setEditingItemId(item.id);
  };

  const saveEditedItem = () => {
    if (!editingItemId) return;
    setCartItems(cartItems.map(item => 
      item.id === editingItemId ? { ...item, quantity: produtoQtd, price: produtoPreco } : item
    ));
    setProdutoSelecionado(null);
    setProdutoQtd(1);
    setProdutoPreco(0);
    setEditingItemId(null);
    toast.success('Item atualizado');
  };

  const cancelEdit = () => {
    setProdutoSelecionado(null);
    setProdutoQtd(1);
    setProdutoPreco(0);
    setEditingItemId(null);
  };

  const updateQuantity = (id, quantity) => {
    if (quantity <= 0) setCartItems(cartItems.filter(item => item.id !== id));
    else setCartItems(cartItems.map(item => item.id === id ? { ...item, quantity } : item));
  };

  const updatePrice = (id, price) => {
    setCartItems(cartItems.map(item => item.id === id ? { ...item, price: parseFloat(price) || 0 } : item));
  };

  const removeFromCart = (id) => {
    setCartItems(cartItems.filter(item => item.id !== id));
    if (editingItemId === id) {
      setProdutoSelecionado(null);
      setEditingItemId(null);
    }
  };

  const updateItemObservacao = (id, observacao) => {
    setCartItems(cartItems.map(item => item.id === id ? { ...item, observacao } : item));
  };

  // Cálculos
  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const descontoCalculado = tipoDesconto === 'percentual' ? (subtotal * (valorDesconto / 100)) : (tipoDesconto === 'valor' ? valorDesconto : 0);
  const total = subtotal - descontoCalculado;
  const totalPago = pagamentos.reduce((sum, p) => sum + (parseFloat(p.valor) || 0), 0);
  const saldoRestante = total - totalPago;

  // Gerar parcelas automaticamente
  useEffect(() => {
    if (!parcelamentoAtivo || saldoRestante <= 0) {
      if (parcelamentoConfig.parcelas_customizadas.length > 0) {
        setParcelamentoConfig(prev => ({ ...prev, parcelas_customizadas: [] }));
      }
      return;
    }
    
    const entrada = parseFloat(parcelamentoConfig.valor_entrada) || 0;
    const valorAParcelar = saldoRestante - entrada;
    if (valorAParcelar <= 0) return;
    
    const numParcelas = parcelamentoConfig.num_parcelas || 2;
    const valorParcela = valorAParcelar / numParcelas;
    const dataBase = parcelamentoConfig.primeira_parcela_data 
      ? new Date(parcelamentoConfig.primeira_parcela_data + 'T12:00:00')
      : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    
    // Só regenerar se o número de parcelas mudou ou se está vazio
    if (parcelamentoConfig.parcelas_customizadas.length !== numParcelas) {
      const novasParcelas = Array.from({ length: numParcelas }).map((_, idx) => {
        const dataVenc = new Date(dataBase);
        dataVenc.setDate(dataVenc.getDate() + (30 * idx));
        return {
          numero: idx + 1,
          valor: Math.round(valorParcela * 100) / 100,
          data_vencimento: dataVenc.toISOString().split('T')[0]
        };
      });
      setParcelamentoConfig(prev => ({ ...prev, parcelas_customizadas: novasParcelas }));
    }
  }, [parcelamentoAtivo, saldoRestante, parcelamentoConfig.valor_entrada, parcelamentoConfig.num_parcelas, parcelamentoConfig.primeira_parcela_data]);
  
  // Atualizar parcela individual
  const handleUpdateParcela = (index, field, value) => {
    setParcelamentoConfig(prev => {
      const novasParcelas = [...prev.parcelas_customizadas];
      novasParcelas[index] = { ...novasParcelas[index], [field]: value };
      return { ...prev, parcelas_customizadas: novasParcelas };
    });
  };
  
  // Recalcular parcelas uniformemente
  const handleRecalcularParcelas = () => {
    const entrada = parseFloat(parcelamentoConfig.valor_entrada) || 0;
    const valorAParcelar = saldoRestante - entrada;
    if (valorAParcelar <= 0) return;
    
    const numParcelas = parcelamentoConfig.num_parcelas || 2;
    const valorParcela = Math.round((valorAParcelar / numParcelas) * 100) / 100;
    const dataBase = parcelamentoConfig.primeira_parcela_data 
      ? new Date(parcelamentoConfig.primeira_parcela_data + 'T12:00:00')
      : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    
    const novasParcelas = Array.from({ length: numParcelas }).map((_, idx) => {
      const dataVenc = new Date(dataBase);
      dataVenc.setDate(dataVenc.getDate() + (30 * idx));
      return {
        numero: idx + 1,
        valor: valorParcela,
        data_vencimento: dataVenc.toISOString().split('T')[0]
      };
    });
    setParcelamentoConfig(prev => ({ ...prev, parcelas_customizadas: novasParcelas }));
  };

  // Buscar CEP
  const buscarCEP = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;
    setBuscandoCEP(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setNovoCliente(prev => ({
          ...prev, endereco: data.logradouro || '', bairro: data.bairro || '',
          cidade: data.localidade || '', estado: data.uf || ''
        }));
      }
    } catch (e) { console.error(e); }
    finally { setBuscandoCEP(false); }
  };

  // Buscar CNPJ
  const buscarCNPJ = async (cnpj) => {
    const cnpjLimpo = cnpj.replace(/\D/g, '');
    if (cnpjLimpo.length !== 14) return;
    setBuscandoCNPJ(true);
    try {
      const res = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpjLimpo}`);
      const data = await res.json();
      if (!data.message) {
        setNovoCliente(prev => ({
          ...prev,
          nome: data.razao_social || data.nome_fantasia || prev.nome,
          email: data.email || prev.email,
          telefone: data.ddd_telefone_1 ? `(${data.ddd_telefone_1.slice(0,2)}) ${data.ddd_telefone_1.slice(2)}` : prev.telefone,
          endereco: `${data.logradouro || ''} ${data.numero || ''}`.trim(),
          bairro: data.bairro || '', cidade: data.municipio || '', estado: data.uf || '',
          cep: data.cep || prev.cep
        }));
        toast.success('CNPJ encontrado!');
      }
    } catch (e) { toast.error('CNPJ não encontrado'); }
    finally { setBuscandoCNPJ(false); }
  };

  // Criar cliente
  const handleCreateCustomer = async () => {
    if (!novoCliente.nome || !novoCliente.telefone) {
      toast.error('Nome e telefone são obrigatórios');
      return;
    }
    
    // Validação de CPF/CNPJ
    if (novoCliente.cpf) {
      if (novoCliente.tipo === 'PF') {
        if (!validateCPF(novoCliente.cpf)) {
          toast.error('CPF inválido! Verifique os números digitados.');
          return;
        }
      } else {
        if (!validateCNPJ(novoCliente.cpf)) {
          toast.error('CNPJ inválido! Verifique os números digitados.');
          return;
        }
      }
    }
    
    setSalvandoCliente(true);
    try {
      const res = await axios.post(`${API}/customers`, {
        nome: novoCliente.nome, 
        email: novoCliente.email || undefined, 
        telefone: novoCliente.telefone,
        cpf_cnpj: novoCliente.cpf || undefined, 
        endereco: novoCliente.endereco || undefined, 
        bairro: novoCliente.bairro || undefined,
        cidade: novoCliente.cidade || undefined, 
        estado: novoCliente.estado || undefined, 
        cep: novoCliente.cep || undefined,
        tipo: novoCliente.tipo
      });
      setCustomers([res.data, ...customers]);
      setSelectedCustomer(res.data);
      setShowNewCustomer(false);
      setNovoCliente({ nome: '', email: '', telefone: '', cpf: '', endereco: '', cidade: '', estado: '', cep: '', tipo: 'PF', bairro: '' });
      toast.success('Cliente cadastrado!');
    } catch (error) { 
      console.error('Erro ao cadastrar cliente:', error);
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar cliente'); 
    }
    finally { setSalvandoCliente(false); }
  };

  // Buscar histórico de pedidos do cliente
  const handleBuscarHistorico = async () => {
    if (!selectedCustomer) return;
    setHistoricoLoading(true);
    setShowHistorico(true);
    try {
      const res = await axios.get(`${API}/customers/${selectedCustomer.id}/orders`);
      // Ordenar por data mais recente primeiro
      const pedidos = (res.data || []).sort((a, b) => {
        const dateA = new Date(b.createdAt || b.data_pedido || 0);
        const dateB = new Date(a.createdAt || a.data_pedido || 0);
        return dateA - dateB;
      });
      setHistoricoPedidos(pedidos);
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
      toast.error('Erro ao carregar histórico do cliente');
    } finally {
      setHistoricoLoading(false);
    }
  };

  // Criar produto
  const handleCreateProduct = async () => {
    if (!novoProduto.nome || !novoProduto.preco) {
      toast.error('Nome e preço são obrigatórios');
      return;
    }
    setSalvandoProduto(true);
    try {
      const produtoData = {
        nome: novoProduto.nome,
        codigo: novoProduto.codigo || `PROD-${Date.now()}`,
        descricao: novoProduto.descricao || '',
        categoria: novoProduto.categoria || '',
        unidade: novoProduto.unidade || 'UN',
        preco_base: parseFloat(novoProduto.preco) || 0,
        custo: parseFloat(novoProduto.custo) || 0,
        estoque: parseInt(novoProduto.estoque_minimo) || 0,
        ncm: novoProduto.ncm || '94036000',
        observacoes: novoProduto.observacoes || '',
        volume_m3: novoProduto.volume_m3 ? parseFloat(novoProduto.volume_m3) : null,
        tipo_madeira_id: novoProduto.tipo_madeira_id || null,
        pecas_madeira: novoProduto.pecas_madeira || [],
        mao_de_obra: novoProduto.mao_de_obra ? parseFloat(novoProduto.mao_de_obra) : null,
        pintura: novoProduto.pintura ? parseFloat(novoProduto.pintura) : null
      };
      
      const res = await axios.post(`${API}/products`, produtoData);
      setProducts([res.data, ...products]);
      addToCart(res.data);
      setShowNewProduct(false);
      setNovoProduto({ nome: '', codigo: '', ncm: '', descricao: '', custo: '', preco: '', categoria: '', unidade: 'UN', estoque_minimo: '', observacoes: '', volume_m3: '', tipo_madeira_id: '', mao_de_obra: '', pintura: '', pecas_madeira: [] });
      setNovaPecaMadeira({ comprimento: '', largura: '', espessura: '', quantidade: '1' });
      toast.success('Produto cadastrado!');
    } catch (error) { 
      console.error('Erro ao cadastrar produto:', error);
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar produto'); 
    }
    finally { setSalvandoProduto(false); }
  };

  // Adicionar pagamento
  const handleAddPagamento = () => {
    if (!pagamentoAtual.valor || parseFloat(pagamentoAtual.valor) <= 0) {
      toast.error('Informe o valor');
      return;
    }
    // Validar bandeira para cartões
    if ((pagamentoAtual.forma_pagamento === 'Cartão Crédito' || pagamentoAtual.forma_pagamento === 'Cartão Débito') && !pagamentoAtual.bandeira) {
      toast.error('Selecione a bandeira do cartão');
      return;
    }
    setPagamentos([...pagamentos, { 
      ...pagamentoAtual, 
      id: `pag_${Date.now()}`, 
      valor: parseFloat(pagamentoAtual.valor),
      status: 'pendente'
    }]);
    setPagamentoAtual({ forma_pagamento: 'PIX', valor: '', conta_id: '', bandeira: '', parcelas: 1, nsu: '', observacao: '', data_pagamento: new Date().toISOString().split('T')[0] });
  };

  // Imprimir usando o layout oficial do sistema principal
  const handlePrint = async () => {
    if (!selectedCustomer || cartItems.length === 0) {
      toast.error('Selecione cliente e produtos');
      return;
    }
    
    // Se já temos um pedido/orçamento salvo (editingOrder), usar endpoint do backend
    if (editingOrder) {
      try {
        const endpoint = tipoDocumento === 'orcamento' 
          ? `${API}/quotes/${editingOrder.id}/pdf`
          : `${API}/orders/${editingOrder.id}/print`;
        
        const response = await axios.get(endpoint, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const printWindow = window.open(url, '_blank');
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
          };
        }
        return;
      } catch (error) {
        console.error('Erro ao gerar PDF:', error);
        toast.error('Erro ao gerar PDF. Usando impressão simplificada.');
      }
    }
    
    // Fallback: impressão simplificada para preview antes de salvar
    const tipoDoc = tipoDocumento === 'orcamento' ? 'ORÇAMENTO' : 'PEDIDO';
    const printContent = `<!DOCTYPE html><html><head><title>${tipoDoc}</title><style>
      *{margin:0;padding:0;box-sizing:border-box}
      body{font-family:Arial,sans-serif;padding:20px;font-size:12px;color:#5f402b}
      .header{background:#ceba9f;padding:20px;margin-bottom:20px;border-bottom:4px solid #5f402b}
      .header h1{font-size:24px;color:#5f402b;margin:0}
      .header .tagline{font-size:12px;color:#5f402b;margin-top:5px}
      .tipo-doc{font-size:18px;font-weight:bold;margin:15px 0;padding:10px;background:${tipoDocumento==='orcamento'?'#fff3cd':'#ceba9f'};text-align:center;border:2px solid #5f402b}
      .info{margin:15px 0}
      .info p{margin:5px 0}
      table{width:100%;border-collapse:collapse;margin:15px 0}
      th,td{border:1px solid #5f402b;padding:10px}
      th{background:#ceba9f;color:#5f402b;text-align:left}
      .text-right{text-align:right}
      .totais{margin-top:20px;border-top:3px solid #5f402b;padding-top:15px}
      .totais .row{display:flex;justify-content:space-between;padding:8px 0;font-size:14px}
      .totais .total{font-size:18px;font-weight:bold;border-top:2px solid #5f402b;padding-top:10px;margin-top:10px}
      .footer{margin-top:30px;text-align:center;font-size:10px;color:#666}
    </style></head><body>
      <div class="header">
        <h1>SERRARIA GUEDES</h1>
        <div class="tagline">PORTAS | JANELAS | MÓVEIS</div>
      </div>
      <div class="tipo-doc">${tipoDoc} - ${nextOrderNumber}</div>
      <div class="info">
        <p><strong>Data:</strong> ${new Date().toLocaleDateString('pt-BR')}</p>
        <p><strong>Cliente:</strong> ${selectedCustomer.nome||selectedCustomer.name}</p>
        <p><strong>Telefone:</strong> ${selectedCustomer.telefone||selectedCustomer.phone||'-'}</p>
        ${selectedCustomer.endereco ? `<p><strong>Endereço:</strong> ${selectedCustomer.endereco}</p>` : ''}
      </div>
      <table>
        <thead><tr><th>Produto</th><th class="text-right">Qtd</th><th class="text-right">Unit.</th><th class="text-right">Subtotal</th></tr></thead>
        <tbody>${cartItems.map(item=>`<tr><td>${item.name||item.nome}${item.observacao?`<br><small><i>${item.observacao}</i></small>`:''}</td><td class="text-right">${item.quantity}</td><td class="text-right">${formatCurrency(item.price)}</td><td class="text-right">${formatCurrency(item.price*item.quantity)}</td></tr>`).join('')}</tbody>
      </table>
      <div class="totais">
        <div class="row"><span>Subtotal:</span><span>${formatCurrency(subtotal)}</span></div>
        ${descontoCalculado>0?`<div class="row"><span>Desconto:</span><span style="color:#c00">-${formatCurrency(descontoCalculado)}</span></div>`:''}
        <div class="row total"><span>TOTAL:</span><span>${formatCurrency(total)}</span></div>
      </div>
      ${observacoes ? `<div style="margin-top:20px;padding:10px;background:#f5f5f5;border-radius:5px"><strong>Observações:</strong><br>${observacoes}</div>` : ''}
      <div class="footer">
        <p>SERRARIA GUEDES LTDA - CNPJ: 20.295.391/0001-26</p>
        <p>AV OTACÍLIO LIRA CABRAL, 57 - (83) 98781-6165 / (83) 98899-8557</p>
      </div>
    </body></html>`;
    const w = window.open('', '_blank');
    w.document.write(printContent);
    w.document.close();
    setTimeout(() => w.print(), 250);
  };

  // Imprimir pedido/orçamento do histórico usando layout oficial
  const handlePrintFromHistory = async (item, tipo) => {
    try {
      const endpoint = tipo === 'orcamento' 
        ? `${API}/quotes/${item.id}/pdf`
        : `${API}/orders/${item.id}/print`;
      
      const response = await axios.get(endpoint, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF para impressão');
    }
  };

  // Finalizar
  const handleFinalizarVenda = async () => {
    if (!selectedCustomer) { toast.error('Selecione um cliente'); return; }
    if (cartItems.length === 0) { toast.error('Adicione produtos'); return; }
    
    setFinalizando(true);
    try {
      let res;
      
      if (tipoDocumento === 'orcamento') {
        // Criar ORÇAMENTO na coleção separada
        const orcamentoData = {
          cliente_id: selectedCustomer.id,
          itens: cartItems.map((item, idx) => ({
            referencia: idx + 1,
            descricao: item.name || item.nome,
            quantidade: item.quantity,
            valor_unitario: item.price,
            total: item.price * item.quantity,
            observacao: item.observacao || ''
          })),
          observacoes: observacoes,
          data: dataPedido,
          validade_dias: 30
        };
        
        res = await axios.post(`${API}/orcamentos`, orcamentoData);
        toast.success(`Orçamento ${res.data.numero} criado!`);
      } else {
        // Criar VENDA/PEDIDO
        const orderData = {
          numero_pedido: nextOrderNumber,
          cliente_id: selectedCustomer.id,
          data_pedido: dataPedido,
          produtos: cartItems.map(item => ({
            produto_id: item.avulso ? null : item.id,
            nome: item.name || item.nome,
            quantidade: item.quantity,
            preco_unitario: item.price,
            custo_unitario: item.custo || 0,
            observacoes: item.observacao || ''
          })),
          forma_pagamento: pagamentos.length > 0 ? pagamentos[0].forma_pagamento : 'A Definir',
          data_entrega_prevista: dataEntrega || new Date().toISOString().split('T')[0],
          observacoes: observacoes,
          pagamentos: pagamentos.map(p => ({
            forma_pagamento: p.forma_pagamento, 
            valor: p.valor, 
            conta_id: p.conta_id || null,
            bandeira: p.bandeira || null, 
            parcelas: p.parcelas || 1, 
            nsu: p.nsu || null,
            observacao: p.observacao || null,
            data_pagamento: p.data_pagamento
          })),
          desconto_tipo: tipoDesconto || null,
          desconto_valor: descontoCalculado || 0,
          status: 'Aguardando',
          origem: 'modulo_vendas',
          criado_por: user.email,
          // Parcelamento para Contas a Receber
          gerar_parcelas: parcelamentoAtivo && !editingOrder,
          num_parcelas: parcelamentoAtivo ? parcelamentoConfig.num_parcelas : undefined,
          valor_entrada: parcelamentoAtivo && parcelamentoConfig.valor_entrada ? parseFloat(parcelamentoConfig.valor_entrada) : undefined,
          primeira_parcela_data: parcelamentoAtivo ? parcelamentoConfig.primeira_parcela_data : undefined,
          parcelas_customizadas: parcelamentoAtivo && parcelamentoConfig.parcelas_customizadas.length > 0 
            ? parcelamentoConfig.parcelas_customizadas 
            : undefined
        };
        
        if (editingOrder) {
          res = await axios.put(`${API}/orders/${editingOrder.id}`, orderData);
          toast.success(`Pedido ${res.data.numero_pedido} atualizado!`);
        } else {
          res = await axios.post(`${API}/orders`, orderData);
          toast.success(`Venda ${res.data.numero_pedido} criada!`);
        }
      }
      
      // Limpar
      setEditingOrder(null);
      setSelectedCustomer(null);
      setCartItems([]);
      setPagamentos([]);
      setObservacoes('');
      setDataEntrega(new Date().toISOString().split('T')[0]);
      setDataPedido(new Date().toISOString().split('T')[0]);
      setValorDesconto(0);
      setTipoDesconto('');
      setParcelamentoAtivo(false);
      setParcelamentoConfig({ num_parcelas: 2, valor_entrada: '', primeira_parcela_data: '', modo_data_parcelas: 'automatico', parcelas_customizadas: [] });
      setCurrentStep(1);
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error('Erro ao finalizar');
    }
    finally { setFinalizando(false); }
  };
  
  // Converter orçamento em pedido
  const handleConverterOrcamento = async (orcamentoId) => {
    setConvertendoId(orcamentoId);
    try {
      const res = await axios.post(`${API}/orcamentos/${orcamentoId}/converter`);
      toast.success(`Orçamento convertido em pedido ${res.data.numero_pedido}!`);
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Erro ao converter orçamento');
    } finally {
      setConvertendoId(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/vendas/login';
  };

  // Editar pedido
  const handleEditOrder = (order) => {
    // Carregar dados do pedido no formulário
    const cliente = customers.find(c => c.id === order.cliente_id);
    if (cliente) {
      setSelectedCustomer(cliente);
    }
    
    // Carregar produtos
    const produtos = order.produtos || order.itens || [];
    setCartItems(produtos.map(p => ({
      id: p.produto_id || p.id || `item_${Date.now()}_${Math.random()}`,
      name: p.nome || p.descricao,
      nome: p.nome || p.descricao,
      quantity: p.quantidade || 1,
      price: p.preco_unitario || p.preco || 0,
      custo: p.custo_unitario || p.custo || 0
    })));
    
    // Carregar pagamentos
    setPagamentos(order.pagamentos || []);
    
    // Carregar desconto
    setTipoDesconto(order.desconto_tipo || '');
    setValorDesconto(order.desconto_valor || 0);
    
    // Carregar observações e data de entrega
    setObservacoes(order.observacoes || '');
    setDataEntrega(order.data_entrega_prevista || new Date().toISOString().split('T')[0]);
    
    // Definir pedido em edição
    setEditingOrder(order);
    setNextOrderNumber(order.numero_pedido);
    
    // Ir para etapa 1
    setCurrentStep(1);
    
    toast.info(`Editando pedido ${order.numero_pedido}`);
  };

  // Excluir pedido
  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Tem certeza que deseja excluir este pedido? Esta ação não pode ser desfeita.')) {
      return;
    }
    
    setDeletingOrderId(orderId);
    try {
      await axios.delete(`${API}/orders/${orderId}`);
      toast.success('Pedido excluído com sucesso!');
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir pedido:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir pedido');
    } finally {
      setDeletingOrderId(null);
    }
  };

  // Excluir orçamento
  const handleDeleteOrcamento = async (orcamentoId) => {
    if (!window.confirm('Tem certeza que deseja excluir este orçamento? Esta ação não pode ser desfeita.')) {
      return;
    }
    
    setDeletingOrcamentoId(orcamentoId);
    try {
      await axios.delete(`${API}/orcamentos/${orcamentoId}`);
      toast.success('Orçamento excluído com sucesso!');
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir orçamento:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir orçamento');
    } finally {
      setDeletingOrcamentoId(null);
    }
  };

  // Cancelar edição
  const handleCancelEdit = () => {
    setEditingOrder(null);
    setSelectedCustomer(null);
    setCartItems([]);
    setPagamentos([]);
    setObservacoes('');
    setDataEntrega(new Date().toISOString().split('T')[0]);
    setValorDesconto(0);
    setTipoDesconto('');
    setCurrentStep(1);
    fetchData(); // Recarregar dados para obter próximo número de pedido
  };

  // Navegação - Orçamento pula etapa 3 (Pagamento)
  const getStepsForType = () => tipoDocumento === 'orcamento' 
    ? ['Cliente', 'Produtos', 'Finalizar'] 
    : ['Cliente', 'Produtos', 'Pagamento', 'Finalizar'];
  
  const stepsForType = getStepsForType();
  const totalStepsForType = stepsForType.length;
  
  const nextStep = () => { 
    if (currentStep < totalStepsForType) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const prevStep = () => { 
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (loading && !user) {
    return <div className="min-h-screen bg-[#f5efe8] flex items-center justify-center"><Loader2 className="h-12 w-12 animate-spin text-[#5f402b]" /></div>;
  }

  // Steps dinâmicos baseados no tipo de documento
  const stepTitles = stepsForType;

  return (
    <div className="min-h-screen bg-[#f5efe8]">
      {/* Header */}
      <header className="bg-[#5f402b] text-white shadow-lg">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShoppingCart className="h-7 w-7" />
            <div>
              <h1 className="text-base sm:text-lg font-bold">Módulo de Vendas</h1>
              <p className="text-[#ceba9f] text-xs hidden sm:block">Serraria Guedes</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="text-[#ceba9f] text-xs sm:text-sm hidden sm:flex items-center"><User className="h-4 w-4 mr-1" />{user?.name}</span>
            <span className="text-[#ceba9f] text-xs sm:hidden flex items-center"><User className="h-3 w-3 mr-0.5" />Vendedor</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-[#4d3422] p-1 sm:p-2">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-2 sm:px-4 py-2 sm:py-4">
        {/* Abas: Nova Venda / Histórico */}
        <div className="bg-white rounded-lg shadow-md p-1 mb-4 flex gap-1">
          <button 
            onClick={() => setAbaAtiva('nova')} 
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-medium text-sm transition-all ${abaAtiva === 'nova' ? 'bg-[#5f402b] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            <Plus className="h-4 w-4" /> Nova Venda
          </button>
          <button 
            onClick={() => setAbaAtiva('historico')} 
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-medium text-sm transition-all ${abaAtiva === 'historico' ? 'bg-[#5f402b] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            <FileText className="h-4 w-4" /> Histórico ({minhasVendas.length + meusOrcamentos.length})
          </button>
        </div>

        {/* Conteúdo da Aba HISTÓRICO */}
        {abaAtiva === 'historico' && (
          <div className="bg-white rounded-lg shadow-md p-4">
            {/* Barra de Pesquisa */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input 
                  placeholder="Pesquisar por número, cliente..." 
                  value={searchHistorico} 
                  onChange={(e) => setSearchHistorico(e.target.value)} 
                  className="pl-10"
                />
              </div>
            </div>

            {/* Seção de Orçamentos */}
            <div className="mb-6">
              <h3 className="text-base font-bold text-amber-700 mb-3 flex items-center gap-2">
                <ClipboardList className="h-5 w-5" /> Orçamentos ({meusOrcamentos.filter(o => 
                  (o.numero || '').toLowerCase().includes(searchHistorico.toLowerCase()) ||
                  (o.cliente_nome || '').toLowerCase().includes(searchHistorico.toLowerCase())
                ).length})
              </h3>
              {meusOrcamentos.filter(o => 
                (o.numero || '').toLowerCase().includes(searchHistorico.toLowerCase()) ||
                (o.cliente_nome || '').toLowerCase().includes(searchHistorico.toLowerCase())
              ).length === 0 ? (
                <p className="text-gray-400 text-center py-4 text-sm">Nenhum orçamento encontrado</p>
              ) : (
                <div className="space-y-2">
                  {meusOrcamentos.filter(o => 
                    (o.numero || '').toLowerCase().includes(searchHistorico.toLowerCase()) ||
                    (o.cliente_nome || '').toLowerCase().includes(searchHistorico.toLowerCase())
                  ).map(orc => (
                    <div key={orc.id} className="p-3 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors border border-amber-200">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-amber-800">{orc.numero}</span>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${orc.status === 'convertido' ? 'bg-green-100 text-green-800' : 'bg-amber-200 text-amber-800'}`}>
                              {orc.status === 'convertido' ? 'Convertido' : 'Pendente'}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">{orc.cliente_nome || 'Cliente avulso'}</div>
                          <div className="text-xs text-gray-400">{formatDate(orc.createdAt || orc.data)}</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-lg font-bold text-amber-800">{formatCurrency(orc.total || 0)}</span>
                          <div className="flex gap-1">
                            {orc.status !== 'convertido' && (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => handleConverterOrcamento(orc.id)}
                                disabled={convertendoId === orc.id}
                                className="h-8 px-2 text-xs bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                              >
                                {convertendoId === orc.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <><Check className="h-3 w-3 mr-1" /> Converter</>
                                )}
                              </Button>
                            )}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handlePrintFromHistory(orc, 'orcamento')}
                              className="h-8 px-2 text-xs bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
                            >
                              <Printer className="h-3 w-3 mr-1" /> Imprimir
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleDeleteOrcamento(orc.id)}
                              disabled={deletingOrcamentoId === orc.id}
                              className="h-8 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50"
                            >
                              {deletingOrcamentoId === orc.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <><Trash2 className="h-3 w-3 mr-1" /> Excluir</>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Seção de Vendas */}
            <div>
              <h3 className="text-base font-bold text-[#5f402b] mb-3 flex items-center gap-2">
                <Receipt className="h-5 w-5" /> Vendas ({minhasVendas.filter(v => 
                  (v.numero_pedido || '').toLowerCase().includes(searchHistorico.toLowerCase()) ||
                  (v.cliente_nome || '').toLowerCase().includes(searchHistorico.toLowerCase())
                ).length})
              </h3>
              {minhasVendas.filter(v => 
                (v.numero_pedido || '').toLowerCase().includes(searchHistorico.toLowerCase()) ||
                (v.cliente_nome || '').toLowerCase().includes(searchHistorico.toLowerCase())
              ).length === 0 ? (
                <p className="text-gray-400 text-center py-4 text-sm">Nenhuma venda encontrada</p>
              ) : (
                <div className="space-y-2">
                  {minhasVendas.filter(v => 
                    (v.numero_pedido || '').toLowerCase().includes(searchHistorico.toLowerCase()) ||
                    (v.cliente_nome || '').toLowerCase().includes(searchHistorico.toLowerCase())
                  ).map(v => (
                    <div key={v.id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-[#5f402b]">{v.numero_pedido}</span>
                            <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">Venda</span>
                          </div>
                          <div className="text-sm text-gray-600">{v.cliente_nome || v.cliente?.nome || 'Cliente avulso'}</div>
                          <div className="text-xs text-gray-400">{formatDate(v.createdAt || v.created_at)}</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-lg font-bold text-[#5f402b]">{formatCurrency(v.total_com_desconto || v.total_venda || v.valor_total || 0)}</span>
                          <div className="flex gap-1">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => { handleEditOrder(v); setAbaAtiva('nova'); }}
                              className="h-8 px-2 text-xs"
                            >
                              <Edit className="h-3 w-3 mr-1" /> Editar
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleDeleteOrder(v.id)}
                              disabled={deletingOrderId === v.id}
                              className="h-8 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50"
                            >
                              {deletingOrderId === v.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <><Trash2 className="h-3 w-3 mr-1" /> Excluir</>
                              )}
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handlePrintFromHistory(v, 'pedido')}
                              className="h-8 px-2 text-xs bg-[#ceba9f]/50 border-[#5f402b]/30 text-[#5f402b] hover:bg-[#ceba9f]"
                            >
                              <Printer className="h-3 w-3 mr-1" /> Imprimir
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Conteúdo da Aba NOVA VENDA */}
        {abaAtiva === 'nova' && (
          <>
        {/* Tipo e Número do Pedido */}
        <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              {!editingOrder ? (
                <div className="flex gap-2">
                  <button onClick={() => setTipoDocumento('venda')} className={`flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg font-medium text-sm transition-all ${tipoDocumento === 'venda' ? 'bg-[#5f402b] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    <Receipt className="h-4 w-4" /> Venda
                  </button>
                  <button onClick={() => setTipoDocumento('orcamento')} className={`flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg font-medium text-sm transition-all ${tipoDocumento === 'orcamento' ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    <ClipboardList className="h-4 w-4" /> Orçamento
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-100 text-blue-800">
                  <Edit className="h-4 w-4" />
                  <span className="font-medium text-sm">Editando</span>
                </div>
              )}
              <div className="border-l pl-3 sm:pl-4 flex items-center gap-3">
                <div>
                  <span className="text-gray-500 text-xs sm:text-sm">{tipoDocumento === 'orcamento' ? 'Nº Orçamento:' : 'Nº Pedido:'}</span>
                  <span className={`ml-1.5 font-bold text-base sm:text-lg ${tipoDocumento === 'orcamento' ? 'text-amber-700' : 'text-[#5f402b]'}`}>
                    {tipoDocumento === 'orcamento' ? nextOrcamentoNumber : nextOrderNumber}
                  </span>
                </div>
                <div className="border-l pl-3">
                  <span className="text-gray-500 text-xs block">Data:</span>
                  <Input 
                    type="date" 
                    value={dataPedido} 
                    onChange={(e) => setDataPedido(e.target.value)} 
                    className="h-7 text-xs w-32 p-1"
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {editingOrder && (
                <Button onClick={handleCancelEdit} variant="outline" size="sm" className="border-red-300 text-red-600 hover:bg-red-50 text-xs sm:text-sm">
                  <X className="h-3 w-3 sm:h-4 sm:w-4 mr-1" /> Cancelar
                </Button>
              )}
              <Button onClick={handlePrint} variant="outline" size="sm" className="border-[#5f402b] text-[#5f402b] hover:bg-[#5f402b] hover:text-white text-xs sm:text-sm">
                <Printer className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" /> <span className="hidden sm:inline">Imprimir</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Progress Steps - Mobile Optimized */}
        <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 mb-4 overflow-x-auto">
          <div className="flex items-center justify-between min-w-[320px]">
            {stepTitles.map((title, idx) => (
              <div key={idx} className="flex items-center">
                <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-sm ${currentStep > idx + 1 ? 'bg-green-500 text-white' : currentStep === idx + 1 ? 'bg-[#5f402b] text-white' : 'bg-gray-200 text-gray-500'}`}>
                  {currentStep > idx + 1 ? <Check className="h-3 w-3 sm:h-4 sm:w-4" /> : idx + 1}
                </div>
                <span className={`ml-1.5 sm:ml-2 text-xs sm:text-sm font-medium hidden sm:inline ${currentStep === idx + 1 ? 'text-[#5f402b]' : 'text-gray-500'}`}>{title}</span>
                {idx < stepTitles.length - 1 && <div className={`w-6 sm:w-12 lg:w-16 h-1 mx-1.5 sm:mx-3 lg:mx-4 ${currentStep > idx + 1 ? 'bg-green-500' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Área Principal */}
          <div className="lg:col-span-2 order-1 lg:order-1">
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 min-h-[300px] sm:min-h-[400px]">
              
              {/* Step 1: Cliente */}
              {currentStep === 1 && (
                <div>
                  <h2 className="text-lg font-bold text-[#5f402b] mb-4 flex items-center gap-2"><User className="h-5 w-5" /> Selecionar Cliente</h2>
                  {selectedCustomer ? (
                    <div className="bg-[#ceba9f]/30 p-4 rounded-lg border border-[#ceba9f]">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-[#5f402b]">{selectedCustomer.nome || selectedCustomer.name}</p>
                          <p className="text-sm text-[#5f402b]/70">{selectedCustomer.telefone || selectedCustomer.phone}</p>
                          {selectedCustomer.endereco && <p className="text-xs text-[#5f402b]/50">{selectedCustomer.endereco}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handleBuscarHistorico}
                            className="border-[#5f402b] text-[#5f402b] hover:bg-[#5f402b]/10"
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            Histórico
                          </Button>
                          <Button variant="ghost" onClick={() => setSelectedCustomer(null)}><X className="h-4 w-4" /></Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input 
                          placeholder="Buscar por nome, telefone ou CPF..." 
                          value={searchCustomer} 
                          onChange={(e) => { 
                            setSearchCustomer(e.target.value); 
                            setShowCustomerDropdown(true); 
                            setHighlightedCustomerIndex(0); 
                          }} 
                          onFocus={() => { 
                            setShowCustomerDropdown(true); 
                            setHighlightedCustomerIndex(0); 
                          }}
                          onKeyDown={(e) => {
                            if (!showCustomerDropdown || filteredCustomers.length === 0) return;
                            
                            if (e.key === 'ArrowDown') {
                              e.preventDefault();
                              const newIndex = Math.min(highlightedCustomerIndex + 1, filteredCustomers.length - 1);
                              setHighlightedCustomerIndex(newIndex);
                              // Scroll para o item
                              const container = customerListRef.current;
                              if (container) {
                                const item = container.children[newIndex];
                                if (item) item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                              }
                            } else if (e.key === 'ArrowUp') {
                              e.preventDefault();
                              const newIndex = Math.max(highlightedCustomerIndex - 1, 0);
                              setHighlightedCustomerIndex(newIndex);
                              // Scroll para o item
                              const container = customerListRef.current;
                              if (container) {
                                const item = container.children[newIndex];
                                if (item) item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                              }
                            } else if (e.key === 'Enter' && highlightedCustomerIndex >= 0) {
                              e.preventDefault();
                              const selected = filteredCustomers[highlightedCustomerIndex];
                              if (selected) {
                                setSelectedCustomer(selected);
                                setSearchCustomer('');
                                setShowCustomerDropdown(false);
                              }
                            } else if (e.key === 'Escape') {
                              setShowCustomerDropdown(false);
                            }
                          }}
                          className="pl-10" 
                        />
                      </div>
                      {showCustomerDropdown && searchCustomer && filteredCustomers.length > 0 && (
                        <div ref={customerListRef} className="border rounded-lg max-h-64 overflow-y-auto">
                          {filteredCustomers.map((c, idx) => (
                            <div 
                              key={c.id} 
                              onClick={() => { setSelectedCustomer(c); setSearchCustomer(''); setShowCustomerDropdown(false); }} 
                              className={`p-3 cursor-pointer border-b last:border-b-0 ${highlightedCustomerIndex === idx ? 'bg-[#5f402b]/20' : 'hover:bg-[#ceba9f]/20'}`}
                            >
                              <p className="font-medium">{c.nome || c.name}</p>
                              <p className="text-sm text-gray-500">{c.telefone || c.phone}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      <Button variant="outline" className="w-full" onClick={() => setShowNewCustomer(true)}><Plus className="h-4 w-4 mr-2" /> Novo Cliente</Button>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Produtos */}
              {currentStep === 2 && (
                <div>
                  <h2 className="text-lg font-bold text-[#5f402b] mb-4 flex items-center gap-2"><Package className="h-5 w-5" /> Produtos</h2>
                  
                  {/* Busca de Produto */}
                  <div className="flex gap-2 mb-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input 
                        ref={productSearchRef}
                        placeholder="Buscar produto..." 
                        value={searchProduct} 
                        onChange={(e) => { 
                          setSearchProduct(e.target.value); 
                          setShowProductSearch(true); 
                          setHighlightedProductIndex(0); 
                        }} 
                        onFocus={() => { 
                          setShowProductSearch(true); 
                          setHighlightedProductIndex(0); 
                        }}
                        onKeyDown={(e) => {
                          if (!showProductSearch || filteredProducts.length === 0) return;
                          
                          if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            const newIndex = Math.min(highlightedProductIndex + 1, filteredProducts.length - 1);
                            setHighlightedProductIndex(newIndex);
                            const container = productListRef.current;
                            if (container) {
                              const item = container.children[newIndex];
                              if (item) item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                            }
                          } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            const newIndex = Math.max(highlightedProductIndex - 1, 0);
                            setHighlightedProductIndex(newIndex);
                            const container = productListRef.current;
                            if (container) {
                              const item = container.children[newIndex];
                              if (item) item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                            }
                          } else if (e.key === 'Enter' && highlightedProductIndex >= 0) {
                            e.preventDefault();
                            const selected = filteredProducts[highlightedProductIndex];
                            if (selected) {
                              selecionarProduto(selected);
                            }
                          } else if (e.key === 'Escape') {
                            setShowProductSearch(false);
                          }
                        }}
                        className="pl-10" 
                      />
                    </div>
                    <Button variant="outline" onClick={() => setShowNewProduct(true)}><Plus className="h-4 w-4 mr-1" /> Novo</Button>
                  </div>
                  
                  {/* Lista de Resultados da Busca */}
                  {showProductSearch && searchProduct && filteredProducts.length > 0 && (
                    <div ref={productListRef} className="border rounded-lg max-h-48 overflow-y-auto mb-4">
                      {filteredProducts.map((p, idx) => (
                        <div 
                          key={p.id} 
                          onClick={() => selecionarProduto(p)} 
                          className={`p-3 cursor-pointer border-b last:border-b-0 flex justify-between ${highlightedProductIndex === idx ? 'bg-[#5f402b]/20' : 'hover:bg-[#ceba9f]/20'}`}
                        >
                          <div><p className="font-medium">{p.nome || p.name}</p><p className="text-xs text-gray-500">{p.codigo || p.code}</p></div>
                          <span className="font-bold text-[#5f402b]">{formatCurrency(p.preco_base || p.preco_venda)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Produto Selecionado - Formulário para Adicionar */}
                  {produtoSelecionado && (
                    <div className="p-4 bg-[#ceba9f]/20 border border-[#5f402b]/30 rounded-lg mb-4 space-y-3">
                      <div className="font-medium text-[#5f402b]">{produtoSelecionado.nome || produtoSelecionado.name}</div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs">Quantidade</Label>
                          <Input 
                            ref={quantityInputRef}
                            type="number" 
                            step="0.01"
                            min="0.01" 
                            value={produtoQtd} 
                            onChange={(e) => setProdutoQtd(parseFloat(e.target.value) || 1)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addToCart();
                              }
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Preço Unit.</Label>
                          <Input 
                            type="number" 
                            step="0.01" 
                            value={produtoPreco} 
                            onChange={(e) => setProdutoPreco(parseFloat(e.target.value) || 0)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addToCart();
                              }
                            }}
                          />
                        </div>
                        <div className="flex items-end gap-2">
                          {editingItemId ? (
                            <>
                              <Button type="button" className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={saveEditedItem}>
                                <Check className="h-4 w-4 mr-1" /> Salvar
                              </Button>
                              <Button type="button" variant="outline" onClick={cancelEdit}>
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            <Button type="button" className="w-full bg-[#5f402b] hover:bg-[#4d3422]" onClick={addToCart}>
                              <Plus className="h-4 w-4 mr-1" /> Adicionar
                            </Button>
                          )}
                        </div>
                      </div>
                      {/* Mostrar total do item antes de adicionar */}
                      {produtoQtd > 0 && produtoPreco > 0 && (
                        <div className="p-2 bg-[#5f402b]/10 rounded-lg text-sm">
                          <div className="flex justify-between">
                            <span>{produtoQtd}x R$ {formatCurrency(produtoPreco).replace('R$ ', '')}</span>
                            <span className="font-bold text-[#5f402b]">Total: {formatCurrency(produtoQtd * produtoPreco)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Lista de Itens do Pedido */}
                  {cartItems.length > 0 && (
                    <div className="border rounded-lg">
                      <div className="px-3 py-2 bg-[#ceba9f]/30 border-b text-sm font-medium text-[#5f402b]">Itens do Pedido</div>
                      {cartItems.map(item => (
                        <div key={item.id} className="px-3 py-2 border-b last:border-b-0 space-y-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-medium">{item.name || item.nome}</span>
                              <span className="text-sm text-gray-500 ml-2">{item.quantity}x R$ {formatCurrency(item.price).replace('R$ ', '')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-[#5f402b]">{formatCurrency(item.price * item.quantity)}</span>
                              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-blue-600" onClick={() => editCartItem(item)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => removeFromCart(item.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          {/* Observação do Produto */}
                          <Input 
                            placeholder="Observação do produto (ex: cor, medida, acabamento...)" 
                            value={item.observacao || ''} 
                            onChange={(e) => updateItemObservacao(item.id, e.target.value)}
                            className="text-xs"
                          />
                        </div>
                      ))}
                      <div className="px-3 py-2 bg-[#ceba9f]/20 flex justify-between font-semibold text-[#5f402b]">
                        <span>Total</span>
                        <span>{formatCurrency(subtotal)}</span>
                      </div>
                    </div>
                  )}

                  {cartItems.length === 0 && !produtoSelecionado && (
                    <div className="text-center text-gray-400 py-8 border rounded-lg">Nenhum produto adicionado</div>
                  )}

                  {/* Desconto - Movido para Etapa 2 */}
                  {cartItems.length > 0 && (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <Label className="text-amber-800 font-medium text-sm mb-2 block">Desconto</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        <Select value={tipoDesconto || 'none'} onValueChange={(v) => { setTipoDesconto(v === 'none' ? '' : v); if(v === 'none') setValorDesconto(0); }}>
                          <SelectTrigger className="text-sm"><SelectValue placeholder="Tipo" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sem desconto</SelectItem>
                            <SelectItem value="percentual">% Percentual</SelectItem>
                            <SelectItem value="valor">R$ Valor</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input 
                          type="number" 
                          step="0.01" 
                          placeholder={tipoDesconto === 'percentual' ? '%' : 'R$'} 
                          value={valorDesconto || ''} 
                          onChange={(e) => setValorDesconto(parseFloat(e.target.value) || 0)} 
                          disabled={!tipoDesconto}
                          className="text-sm"
                        />
                        <div className="flex items-center text-sm text-amber-700 font-medium col-span-2 sm:col-span-1">
                          {descontoCalculado > 0 && `-${formatCurrency(descontoCalculado)}`}
                        </div>
                      </div>
                      {/* Mostrar equivalência: se foi % mostra valor, se foi valor mostra % */}
                      {tipoDesconto && valorDesconto > 0 && descontoCalculado > 0 && (
                        <div className="text-xs text-red-600 text-right font-medium mt-2">
                          {tipoDesconto === 'percentual' ? (
                            <>Desconto de {valorDesconto}% = <strong>{formatCurrency(descontoCalculado)}</strong></>
                          ) : (
                            <>Desconto de {formatCurrency(descontoCalculado)} = <strong>{subtotal > 0 ? ((descontoCalculado / subtotal) * 100).toFixed(2) : 0}%</strong></>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Pagamento - Somente para Vendas */}
              {currentStep === 3 && tipoDocumento === 'venda' && (
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-[#5f402b] mb-4 flex items-center gap-2"><CreditCard className="h-5 w-5" /> Pagamento</h2>

                  {/* Formulário de Pagamento */}
                  <div className="space-y-3 mb-4 p-3 bg-gray-50 rounded-lg border">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Forma de Pagamento</Label>
                        <Select value={pagamentoAtual.forma_pagamento} onValueChange={(v) => {
                          // Auto-selecionar conta de cartão quando escolher Cartão Crédito ou Débito
                          let contaId = '';
                          if (v === 'Cartão Crédito' || v === 'Cartão Débito') {
                            // Buscar conta com conta_cartao === true
                            const contaCartao = bankAccounts.find(acc => acc.conta_cartao === true);
                            if (contaCartao) contaId = contaCartao.id;
                          }
                          setPagamentoAtual({...pagamentoAtual, forma_pagamento: v, conta_id: contaId, bandeira: '', parcelas: 1, nsu: ''});
                        }}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="PIX">PIX</SelectItem>
                            <SelectItem value="Dinheiro">Dinheiro</SelectItem>
                            <SelectItem value="Cartão Crédito">Cartão Crédito</SelectItem>
                            <SelectItem value="Cartão Débito">Cartão Débito</SelectItem>
                            <SelectItem value="Boleto">Boleto</SelectItem>
                            <SelectItem value="Transferência">Transferência</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Conta Destino</Label>
                        <Select value={pagamentoAtual.conta_id || 'none'} onValueChange={(v) => setPagamentoAtual({...pagamentoAtual, conta_id: v === 'none' ? '' : v})}>
                          <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sem conta específica</SelectItem>
                            {bankAccounts.map(acc => (
                              <SelectItem key={acc.id} value={acc.id}>{acc.nome || acc.name} - {acc.banco}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Campos específicos para cartão */}
                    {(pagamentoAtual.forma_pagamento === 'Cartão Crédito' || pagamentoAtual.forma_pagamento === 'Cartão Débito') && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        <div>
                          <Label className="text-xs">Bandeira *</Label>
                          <Select value={pagamentoAtual.bandeira || 'none'} onValueChange={(v) => setPagamentoAtual({...pagamentoAtual, bandeira: v === 'none' ? '' : v})}>
                            <SelectTrigger className="text-sm"><SelectValue placeholder="Bandeira" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Selecione...</SelectItem>
                              <SelectItem value="Visa">Visa</SelectItem>
                              <SelectItem value="Mastercard">Mastercard</SelectItem>
                              <SelectItem value="Elo">Elo</SelectItem>
                              <SelectItem value="Amex">Amex</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {pagamentoAtual.forma_pagamento === 'Cartão Crédito' && (
                          <div>
                            <Label className="text-xs">Parcelas</Label>
                            <Select value={String(pagamentoAtual.parcelas || 1)} onValueChange={(v) => setPagamentoAtual({...pagamentoAtual, parcelas: parseInt(v)})}>
                              <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                                  <SelectItem key={n} value={String(n)}>{n}x</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div className={pagamentoAtual.forma_pagamento === 'Cartão Débito' ? 'col-span-2 sm:col-span-1' : ''}>
                          <Label className="text-xs">NSU</Label>
                          <Input placeholder="Nº Seq." value={pagamentoAtual.nsu || ''} onChange={(e) => setPagamentoAtual({...pagamentoAtual, nsu: e.target.value})} className="text-sm" />
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs">Valor *</Label>
                        <Input type="number" step="0.01" placeholder="0,00" value={pagamentoAtual.valor} onChange={(e) => setPagamentoAtual({...pagamentoAtual, valor: e.target.value})} className="text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs">Data</Label>
                        <Input type="date" value={pagamentoAtual.data_pagamento} onChange={(e) => setPagamentoAtual({...pagamentoAtual, data_pagamento: e.target.value})} className="text-sm" />
                      </div>
                      <div className="flex items-end col-span-2 sm:col-span-1">
                        <Button onClick={handleAddPagamento} className="w-full bg-[#5f402b] hover:bg-[#4d3422] text-sm"><Plus className="h-4 w-4 mr-1" /> Adicionar</Button>
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs">Observação do Pagamento</Label>
                      <Input placeholder="Ex: Comprovante recebido, cheque nº..." value={pagamentoAtual.observacao || ''} onChange={(e) => setPagamentoAtual({...pagamentoAtual, observacao: e.target.value})} className="text-sm" />
                    </div>
                  </div>

                  {/* Lista de Pagamentos */}
                  {pagamentos.length > 0 && (
                    <div className="space-y-2 mb-4">
                      <Label className="text-sm font-medium">Pagamentos Adicionados</Label>
                      {pagamentos.map((p, idx) => (
                        <div key={p.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-[#ceba9f]/20 rounded-lg gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-[#5f402b] text-sm">{p.forma_pagamento}</div>
                            <div className="text-xs text-gray-500 truncate">
                              {formatDate(p.data_pagamento)}
                              {p.bandeira && ` • ${p.bandeira}`}
                              {p.parcelas > 1 && ` • ${p.parcelas}x`}
                              {p.nsu && ` • NSU: ${p.nsu}`}
                            </div>
                            {p.observacao && <div className="text-xs text-gray-400 italic truncate">{p.observacao}</div>}
                          </div>
                          <div className="flex items-center justify-between sm:justify-end gap-2">
                            <span className="font-bold text-[#5f402b] text-base sm:text-lg">{formatCurrency(p.valor)}</span>
                            <Button variant="ghost" size="sm" onClick={() => setPagamentos(pagamentos.filter((_,i) => i !== idx))}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Resumo Financeiro */}
                  <div className="p-3 sm:p-4 bg-[#5f402b]/10 rounded-lg border border-[#5f402b]/20">
                    <div className="flex justify-between text-sm"><span>Subtotal:</span><span>{formatCurrency(subtotal)}</span></div>
                    {descontoCalculado > 0 && <div className="flex justify-between text-sm text-red-600"><span>Desconto:</span><span>-{formatCurrency(descontoCalculado)}</span></div>}
                    <div className="flex justify-between font-bold text-base sm:text-lg border-t border-[#5f402b]/20 pt-2 mt-2"><span>Total:</span><span className="text-[#5f402b]">{formatCurrency(total)}</span></div>
                    <div className="flex justify-between text-green-600 mt-1 text-sm"><span>Total Pago:</span><span className="font-bold">{formatCurrency(totalPago)}</span></div>
                    <div className="flex justify-between text-orange-600 text-sm"><span>Restante:</span><span className="font-bold">{formatCurrency(saldoRestante)}</span></div>
                  </div>

                  {/* Parcelamento (Contas a Receber) */}
                  {!editingOrder && saldoRestante > 0 && (
                    <div className="mt-4 p-3 sm:p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
                      <div className="flex items-center gap-2 mb-3">
                        <Receipt className="h-4 w-4 text-amber-600" />
                        <span className="font-semibold text-sm text-amber-700">Parcelamento (Contas a Receber)</span>
                        <Switch
                          checked={parcelamentoAtivo}
                          onCheckedChange={(checked) => setParcelamentoAtivo(checked)}
                          className="ml-auto"
                        />
                      </div>
                      
                      {parcelamentoAtivo && (
                        <div className="space-y-3">
                          <p className="text-xs text-muted-foreground">
                            Gera parcelas automáticas no Contas a Receber para o saldo restante de <strong>{formatCurrency(saldoRestante)}</strong>
                          </p>
                          
                          {/* Valor de Entrada e Parcelas */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Entrada (opcional)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="R$ 0,00"
                                value={parcelamentoConfig.valor_entrada}
                                onChange={(e) => setParcelamentoConfig({ ...parcelamentoConfig, valor_entrada: e.target.value })}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Nº de Parcelas</Label>
                              <Select 
                                value={String(parcelamentoConfig.num_parcelas)} 
                                onValueChange={(v) => setParcelamentoConfig({ ...parcelamentoConfig, num_parcelas: parseInt(v) })}
                              >
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                                    <SelectItem key={n} value={String(n)}>{n}x</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          {/* Modo de data */}
                          <div>
                            <Label className="text-xs">Data da 1ª parcela</Label>
                            <div className="grid grid-cols-2 gap-2 mt-1">
                              <Button
                                type="button"
                                variant={parcelamentoConfig.modo_data_parcelas === 'automatico' ? 'default' : 'outline'}
                                size="sm"
                                className="text-xs"
                                onClick={() => setParcelamentoConfig({ ...parcelamentoConfig, modo_data_parcelas: 'automatico', primeira_parcela_data: '' })}
                              >
                                <CalendarClock className="h-3 w-3 mr-1" />
                                Auto (30 dias)
                              </Button>
                              <Button
                                type="button"
                                variant={parcelamentoConfig.modo_data_parcelas === 'manual' ? 'default' : 'outline'}
                                size="sm"
                                className="text-xs"
                                onClick={() => setParcelamentoConfig({ ...parcelamentoConfig, modo_data_parcelas: 'manual' })}
                              >
                                <Calendar className="h-3 w-3 mr-1" />
                                Definir Data
                              </Button>
                            </div>
                          </div>
                          
                          {parcelamentoConfig.modo_data_parcelas === 'manual' && (
                            <div>
                              <Label className="text-xs">Data 1ª Parcela</Label>
                              <Input
                                type="date"
                                value={parcelamentoConfig.primeira_parcela_data}
                                onChange={(e) => setParcelamentoConfig({ ...parcelamentoConfig, primeira_parcela_data: e.target.value })}
                              />
                            </div>
                          )}
                          
                          {/* Parcelas Editáveis */}
                          {parcelamentoConfig.parcelas_customizadas.length > 0 && (
                            <div className="border rounded-lg bg-white/50 p-2 space-y-2">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-muted-foreground">Parcelas (editáveis):</span>
                                <Button 
                                  type="button" 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-6 text-xs text-blue-600"
                                  onClick={handleRecalcularParcelas}
                                >
                                  Recalcular
                                </Button>
                              </div>
                              {parseFloat(parcelamentoConfig.valor_entrada) > 0 && (
                                <div className="flex items-center gap-2 text-xs py-1 px-2 bg-green-50 rounded border border-green-200">
                                  <span className="text-green-700 font-medium flex-1">Entrada</span>
                                  <span className="font-semibold text-green-700">{formatCurrency(parseFloat(parcelamentoConfig.valor_entrada))}</span>
                                </div>
                              )}
                              {parcelamentoConfig.parcelas_customizadas.map((parcela, idx) => (
                                <div key={`parcela-${parcela.numero}-${idx}`} className="flex items-center gap-2 py-1 border-b last:border-b-0">
                                  <span className="text-xs font-medium w-12 shrink-0">
                                    {parcela.numero}/{parcelamentoConfig.num_parcelas}
                                  </span>
                                  <Input
                                    type="date"
                                    className="h-7 text-xs flex-1"
                                    value={parcela.data_vencimento}
                                    onChange={(e) => handleUpdateParcela(idx, 'data_vencimento', e.target.value)}
                                  />
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs text-muted-foreground">R$</span>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      className="h-7 text-xs w-20"
                                      value={parcela.valor}
                                      onChange={(e) => handleUpdateParcela(idx, 'valor', parseFloat(e.target.value) || 0)}
                                    />
                                  </div>
                                </div>
                              ))}
                              {/* Total das parcelas */}
                              <div className="flex justify-between text-xs pt-2 border-t font-semibold">
                                <span>Total das parcelas:</span>
                                <span className={
                                  Math.abs(parcelamentoConfig.parcelas_customizadas.reduce((s, p) => s + p.valor, 0) - (saldoRestante - (parseFloat(parcelamentoConfig.valor_entrada) || 0))) > 0.01 
                                    ? 'text-red-600' 
                                    : 'text-green-600'
                                }>
                                  {formatCurrency(parcelamentoConfig.parcelas_customizadas.reduce((s, p) => s + p.valor, 0))}
                                </span>
                              </div>
                              {Math.abs(parcelamentoConfig.parcelas_customizadas.reduce((s, p) => s + p.valor, 0) - (saldoRestante - (parseFloat(parcelamentoConfig.valor_entrada) || 0))) > 0.01 && (
                                <div className="text-xs text-red-500 text-center">
                                  Diferença de {formatCurrency(Math.abs(parcelamentoConfig.parcelas_customizadas.reduce((s, p) => s + p.valor, 0) - (saldoRestante - (parseFloat(parcelamentoConfig.valor_entrada) || 0))))} do valor a parcelar
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Step Finalizar - Último step (3 para orçamento, 4 para venda) */}
              {currentStep === totalStepsForType && (
                <div>
                  <h2 className="text-lg font-bold text-[#5f402b] mb-4 flex items-center gap-2"><FileText className="h-5 w-5" /> Resumo do {tipoDocumento === 'orcamento' ? 'Orçamento' : 'Pedido'}</h2>
                  <div className="space-y-4">
                    <div className="p-3 bg-[#ceba9f]/20 rounded-lg">
                      <div className="font-medium text-[#5f402b]">Cliente</div>
                      <div>{selectedCustomer?.nome || selectedCustomer?.name || '-'}</div>
                      <div className="text-sm text-gray-500">{selectedCustomer?.telefone || selectedCustomer?.phone}</div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="font-medium text-gray-700">Produtos ({cartItems.length})</div>
                      {cartItems.map(item => (
                        <div key={item.id} className="py-1 border-b last:border-b-0">
                          <div className="flex justify-between text-sm">
                            <span>{item.quantity}x {item.name || item.nome}</span>
                            <span>{formatCurrency(item.price * item.quantity)}</span>
                          </div>
                          {item.observacao && <div className="text-xs text-gray-500 italic">{item.observacao}</div>}
                        </div>
                      ))}
                    </div>
                    {/* Resumo financeiro */}
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex justify-between text-sm"><span>Subtotal:</span><span>{formatCurrency(subtotal)}</span></div>
                      {descontoCalculado > 0 && <div className="flex justify-between text-sm text-red-600"><span>Desconto:</span><span>-{formatCurrency(descontoCalculado)}</span></div>}
                      <div className="flex justify-between font-bold text-lg border-t border-amber-300 pt-2 mt-2"><span>Total:</span><span className={tipoDocumento === 'orcamento' ? 'text-amber-800' : 'text-[#5f402b]'}>{formatCurrency(total)}</span></div>
                      {tipoDocumento === 'venda' && totalPago > 0 && (
                        <>
                          <div className="flex justify-between text-sm text-green-600 mt-1"><span>Pago:</span><span>{formatCurrency(totalPago)}</span></div>
                          <div className="flex justify-between text-sm text-orange-600"><span>Restante:</span><span>{formatCurrency(saldoRestante)}</span></div>
                        </>
                      )}
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                      {tipoDocumento === 'venda' ? (
                        <div>
                          <Label>Data de Entrega</Label>
                          <Input type="date" value={dataEntrega} onChange={(e) => setDataEntrega(e.target.value)} />
                        </div>
                      ) : (
                        <div>
                          <Label>Validade do Orçamento</Label>
                          <Input type="date" defaultValue={new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0]} />
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>Observações</Label>
                      <Textarea value={observacoes} onChange={(e) => setObservacoes(e.target.value)} placeholder="Observações gerais..." rows={2} />
                    </div>
                  </div>
                </div>
              )}

              {/* Navegação */}
              <div className="flex justify-between mt-4 sm:mt-6 pt-4 border-t gap-2">
                <Button variant="outline" onClick={prevStep} disabled={currentStep === 1} size="sm" className="text-xs sm:text-sm">
                  <ChevronLeft className="h-4 w-4 mr-0.5 sm:mr-1" /> <span className="hidden sm:inline">Anterior</span>
                </Button>
                {currentStep < totalStepsForType ? (
                  <Button onClick={nextStep} className="bg-[#5f402b] hover:bg-[#4d3422] text-white text-xs sm:text-sm" size="sm">
                    Próximo <ChevronRight className="h-4 w-4 ml-0.5 sm:ml-1" />
                  </Button>
                ) : (
                  <Button onClick={handleFinalizarVenda} disabled={finalizando} size="sm" className={`${editingOrder ? 'bg-blue-600 hover:bg-blue-700' : tipoDocumento === 'orcamento' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#5f402b] hover:bg-[#4d3422]'} text-white text-xs sm:text-sm`}>
                    {finalizando ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                    <span className="hidden sm:inline">{editingOrder ? 'Salvar Alterações' : tipoDocumento === 'orcamento' ? 'Gerar Orçamento' : 'Finalizar Venda'}</span>
                    <span className="sm:hidden">{editingOrder ? 'Salvar' : tipoDocumento === 'orcamento' ? 'Gerar' : 'Finalizar'}</span>
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Resumo Lateral */}
          <div className="space-y-4 order-2 lg:order-2">
            <Card className={`border-0 shadow-md ${tipoDocumento === 'orcamento' ? 'bg-amber-50' : 'bg-[#ceba9f]/30'}`}>
              <CardHeader className="pb-2 p-3 sm:p-4">
                <CardTitle className={`text-base sm:text-lg ${tipoDocumento === 'orcamento' ? 'text-amber-800' : 'text-[#5f402b]'}`}>
                  {tipoDocumento === 'orcamento' ? 'Orçamento' : 'Resumo'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 sm:space-y-2 p-3 sm:p-4 pt-0">
                <div className="flex justify-between text-xs sm:text-sm"><span>Itens:</span><span>{cartItems.length}</span></div>
                <div className="flex justify-between text-xs sm:text-sm"><span>Subtotal:</span><span>{formatCurrency(subtotal)}</span></div>
                {descontoCalculado > 0 && <div className="flex justify-between text-xs sm:text-sm text-red-600"><span>Desconto:</span><span>-{formatCurrency(descontoCalculado)}</span></div>}
                <div className="flex justify-between font-bold text-base sm:text-lg border-t pt-2"><span>Total:</span><span className={tipoDocumento === 'orcamento' ? 'text-amber-800' : 'text-[#5f402b]'}>{formatCurrency(total)}</span></div>
                {totalPago > 0 && (
                  <>
                    <div className="flex justify-between text-xs sm:text-sm text-green-600"><span>Pago:</span><span>{formatCurrency(totalPago)}</span></div>
                    <div className="flex justify-between text-xs sm:text-sm text-orange-600"><span>Restante:</span><span>{formatCurrency(saldoRestante)}</span></div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        </>
        )}
      </main>

      {/* Modal Histórico do Cliente */}
      <Dialog open={showHistorico} onOpenChange={setShowHistorico}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-[#5f402b] flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Histórico de Compras - {selectedCustomer?.nome || selectedCustomer?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {historicoLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[#5f402b]" />
              </div>
            ) : historicoPedidos.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Nenhum pedido encontrado para este cliente</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Lista de Pedidos em Cards */}
                {historicoPedidos.map(pedido => {
                  const itens = pedido.produtos || pedido.items || pedido.itens || [];
                  const subtotal = itens.reduce((acc, item) => acc + (item.total || item.subtotal || (item.quantidade * (item.preco_unitario || item.preco_venda)) || 0), 0);
                  const desconto = pedido.desconto_valor || pedido.desconto || 0;
                  const total = pedido.total_com_desconto || pedido.total || pedido.total_venda || (subtotal - desconto);
                  
                  return (
                    <div key={pedido.id} className="border rounded-lg p-4 bg-gray-50/50 hover:bg-gray-50 transition-colors">
                      {/* Header do Pedido */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-[#5f402b]">
                            {pedido.numero_pedido || pedido.numero || `PED-${pedido.id?.substring(0, 8).toUpperCase()}`}
                          </span>
                          <Badge 
                            variant="outline"
                            className={`text-xs ${
                              pedido.status === 'finalizado' || pedido.status === 'entregue' 
                                ? 'bg-green-100 text-green-700 border-green-300' 
                                : pedido.status === 'cancelado' 
                                  ? 'bg-red-100 text-red-700 border-red-300'
                                  : 'bg-yellow-100 text-yellow-700 border-yellow-300'
                            }`}
                          >
                            {pedido.status === 'finalizado' ? 'Finalizado' : 
                             pedido.status === 'entregue' ? 'Entregue' :
                             pedido.status === 'cancelado' ? 'Cancelado' : 'Aguardando'}
                          </Badge>
                        </div>
                        <span className="text-sm text-gray-500">
                          {new Date(pedido.createdAt || pedido.data_pedido).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                      
                      {/* Itens do Pedido */}
                      {itens.length > 0 ? (
                        <div className="space-y-1 mb-3">
                          {itens.map((item, idx) => {
                            const precoUnit = item.preco_unitario || item.preco_venda || 0;
                            const qtd = item.quantidade || 1;
                            const totalItem = item.total || item.subtotal || (qtd * precoUnit) || 0;
                            return (
                              <div key={idx} className="text-sm text-gray-600 flex justify-between">
                                <span>
                                  {qtd}x {item.produto_nome || item.nome || 'Produto'} 
                                  {item.largura && item.altura ? ` ${item.largura} x ${item.altura}` : ''}
                                  {precoUnit > 0 ? ` (R$ ${precoUnit.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} un.)` : ''}
                                </span>
                                <span className="font-medium">
                                  R$ {totalItem.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400 mb-3">Sem itens detalhados</p>
                      )}
                      
                      {/* Desconto e Total */}
                      <div className="border-t pt-2 space-y-1">
                        {desconto > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-red-500">Desconto:</span>
                            <span className="text-red-500 font-medium">-R$ {desconto.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="font-medium"></span>
                          <span className="font-bold text-lg text-[#5f402b]">
                            R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Rodapé */}
                <div className="text-center text-sm text-gray-500 pt-2 border-t">
                  {historicoPedidos.length} pedido(s) encontrado(s)
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Novo Cliente - Completo */}
      <Dialog open={showNewCustomer} onOpenChange={setShowNewCustomer}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="text-[#5f402b]">Cadastrar Novo Cliente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {/* Tipo de Pessoa */}
            <div className="flex gap-4 p-2 bg-gray-50 rounded-lg">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={novoCliente.tipo === 'PF'} onChange={() => setNovoCliente({...novoCliente, tipo: 'PF', cpf: ''})} className="accent-[#5f402b]" />
                <span className="text-sm font-medium">Pessoa Física</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={novoCliente.tipo === 'PJ'} onChange={() => setNovoCliente({...novoCliente, tipo: 'PJ', cpf: ''})} className="accent-[#5f402b]" />
                <span className="text-sm font-medium">Pessoa Jurídica</span>
              </label>
            </div>

            {/* CPF/CNPJ com busca */}
            <div>
              <Label>{novoCliente.tipo === 'PF' ? 'CPF' : 'CNPJ'}</Label>
              <div className="flex gap-2">
                <Input 
                  value={novoCliente.cpf} 
                  onChange={(e) => setNovoCliente({...novoCliente, cpf: novoCliente.tipo === 'PF' ? formatCPF(e.target.value) : formatCNPJ(e.target.value)})} 
                  placeholder={novoCliente.tipo === 'PF' ? '000.000.000-00' : '00.000.000/0000-00'}
                />
                {novoCliente.tipo === 'PJ' && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => buscarCNPJ(novoCliente.cpf)} 
                    disabled={buscandoCNPJ || novoCliente.cpf.replace(/\D/g, '').length !== 14}
                    className="shrink-0"
                  >
                    {buscandoCNPJ ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Buscar'}
                  </Button>
                )}
              </div>
            </div>

            {/* Nome e Telefone */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome *</Label>
                <Input value={novoCliente.nome} onChange={(e) => setNovoCliente({...novoCliente, nome: e.target.value})} placeholder="Nome completo" />
              </div>
              <div>
                <Label>Telefone *</Label>
                <Input value={novoCliente.telefone} onChange={(e) => setNovoCliente({...novoCliente, telefone: formatPhone(e.target.value)})} placeholder="(00) 00000-0000" />
              </div>
            </div>

            {/* Email */}
            <div>
              <Label>Email</Label>
              <Input value={novoCliente.email} onChange={(e) => setNovoCliente({...novoCliente, email: e.target.value})} placeholder="email@exemplo.com" type="email" />
            </div>

            {/* CEP e Endereço */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>CEP</Label>
                <div className="flex gap-1">
                  <Input 
                    value={novoCliente.cep} 
                    onChange={(e) => { 
                      const cep = e.target.value.replace(/\D/g,'').slice(0,8); 
                      const cepFormatado = cep.replace(/(\d{5})(\d)/, '$1-$2');
                      setNovoCliente({...novoCliente, cep: cepFormatado}); 
                      if(cep.length===8) buscarCEP(cep); 
                    }} 
                    placeholder="00000-000"
                  />
                </div>
              </div>
              <div className="col-span-2">
                <Label>Endereço</Label>
                <Input value={novoCliente.endereco} onChange={(e) => setNovoCliente({...novoCliente, endereco: e.target.value})} placeholder="Rua, número, complemento" />
              </div>
            </div>

            {/* Bairro, Cidade, UF */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Bairro</Label>
                <Input value={novoCliente.bairro} onChange={(e) => setNovoCliente({...novoCliente, bairro: e.target.value})} placeholder="Bairro" />
              </div>
              <div>
                <Label>Cidade</Label>
                <Input value={novoCliente.cidade} onChange={(e) => setNovoCliente({...novoCliente, cidade: e.target.value})} placeholder="Cidade" />
              </div>
              <div>
                <Label>UF</Label>
                <Select value={novoCliente.estado} onValueChange={(v) => setNovoCliente({...novoCliente, estado: v})}>
                  <SelectTrigger><SelectValue placeholder="UF" /></SelectTrigger>
                  <SelectContent>
                    {ESTADOS_BRASIL.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {buscandoCEP && <p className="text-xs text-amber-600">Buscando CEP...</p>}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowNewCustomer(false)}>Cancelar</Button>
            <Button onClick={handleCreateCustomer} disabled={salvandoCliente} className="bg-[#5f402b] hover:bg-[#4d3422]">
              {salvandoCliente ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Cadastrar Cliente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Novo Produto - Usando Componente Reutilizável */}
      <ProductModal
        open={showNewProduct}
        onOpenChange={setShowNewProduct}
        onProductCreated={(newProduct) => {
          setProducts([...products, newProduct]);
          toast.success('Produto cadastrado com sucesso!');
        }}
        categories={productCategories}
        tiposMadeira={tiposMadeira}
      />
    </div>
  );
}
